<template>
  <ContractEditDialog
    v-if="contractToEdit"
    v-model:visible="contractEditDialogVisible"
    :contract="contractToEdit"
    :license-model-id="license?._id"
    :method="contractEditMethod"
    :is-fallback="isFallback"
    @cancelled="contractToEdit = null"
    @saved="handleContractSave()" />

  <DeleteConfirmDialog
    v-if="contractToDelete"
    v-model:visible="contractDeleteDialogVisible"
    :loading="contractDeleteLoading"
    :title="
      i18n.t('dialogs.licenceModelEditDialog.licenseModelForm.deletePriceModel')
    "
    :sub-title="
      i18n.t(
        'dialogs.licenceModelEditDialog.licenseModelForm.deletePriceModelWarning',
        { description: contractToDelete?.description }
      )
    "
    @cancel="contractDeleteDialogVisible = false"
    @delete="removeContract(contractToDelete!)" />

  <SmDrawer
    v-model:visibility="localVisibility"
    :title="i18n.t('licensemodel')">
    <!-- Header -->
    <template #close>
      <div class="header">
        <div
          v-if="license"
          class="mr-2 flex items-center"
          @click="handleLicenseDelete">
          <Trash :size="20" class="cursor-pointer hover:text-magenta" />
        </div>
        <SmButtonClose
          data-test-id="user-detail-drawer-close-button"
          @click="localVisibility = false" />
      </div>
    </template>

    <!-- Drawer Content -->
    <template #default>
      <!-- Information Card -->
      <LicenseModelInformations
        v-if="license"
        class="mb-3"
        :license="license"
        @edit="emit('edit', license)" />

      <!-- Analytics Card -->
      <LicenseModelAnalytics
        v-if="license && softwareLicenseCosts"
        class="mb-3"
        :active-contracts="activeContracts"
        :application-id="props.applicationId"
        :license-id="license._id"
        :total-quota="totalQuota"
        :external-quota="externalQuota"
        :manual-active-quota="manualActiveQuota"
        :costs="softwareLicenseCosts"
        @create-fallback="openCreateFallbackContractDialog()" />

      <!--Alerts-->
      <SmDialogMessage
        class="mb-3"
        type="warning"
        :message="i18n.t('pricemodelMessageFallbackExceeded')"
        :visible="showAlertExternalExceeded" />

      <SmDialogMessage
        v-if="showAlertExternalMissingContracts"
        class="mb-3"
        type="warning"
        :message="i18n.t('pricemodelMessageNoFallback')" />

      <!-- External Quota Contract -->
      <LicenseModelFallbackSelect
        v-if="
          !showAlertExternalExceeded &&
          softwareLicenseCosts &&
          externalQuota > 0
        "
        class="mb-3"
        :external-quota="externalQuota"
        :contracts="activeContracts"
        :costs="softwareLicenseCosts"
        @create-fallback="openCreateFallbackContractDialog()" />

      <!-- Contract List -->
      <div>
        <div class="mb-4 mt-8 flex items-center justify-between">
          <h2>{{ i18n.t('priceModel') }}</h2>
          <SmButton outline @click="openCreateContractDialog">
            <Plus class="mr-2" :size="18" />
            {{ i18n.t('createPriceModel') }}
          </SmButton>
        </div>
        <div v-if="contracts && contracts.length > 0">
          <div
            v-for="contract in contracts"
            :key="contract._id"
            class="contract-item mb-4 flex items-center justify-between gap-2 border-b pb-4">
            <div>
              <TextMultiline
                v-if="contract.description"
                class="font-bold"
                :show-tool-tip="true">
                <h3>{{ contract.description }}</h3>
              </TextMultiline>
              <div class="flex items-center">
                <SmTag
                  v-if="contractIsActive(contract) === 'deprecated'"
                  :label="i18n.t('deprecated')"
                  class="mr-2 text-sm" />
                <SmTag
                  v-else-if="contractIsActive(contract) === 'upcoming'"
                  :label="i18n.t('upcoming')"
                  class="mr-2 text-sm" />
                <p>{{ contractDetailText(contract) }}</p>
              </div>
            </div>
            <div class="flex gap-2">
              <SquarePen
                :size="20"
                class="hover:cursor-pointer hover:text-gray-900 hover:dark:text-gray-400"
                @click="handleEditContract(contract)" />
              <Trash
                :size="20"
                class="cursor-pointer hover:text-magenta"
                @click="handleDeleteContract(contract)" />
            </div>
          </div>
        </div>
        <div
          v-else
          class="flex h-36 items-center justify-center rounded border-2 border-dashed border-gray-300">
          <!-- Empty State -->
          <p class="text-contrast-muted">
            {{ i18n.t('drawers.licenceDetailDrawer.noPriceFound') }}
          </p>
        </div>
      </div>
    </template>
  </SmDrawer>
</template>

<script lang="ts" setup>
  import { Plus, SquarePen } from 'lucide-vue-next'
  import { useI18n } from 'vue-i18n'
  import {
    SoftwareLicense,
    BillingUnit,
    BillingPeriod,
    PriceInformation,
    PriceInformationPostIn,
    SoftwareLicenseCost,
  } from '@/client'
  import { useContractStore } from '@/stores/contractStore'
  import { useLicenseStore } from '@/stores/license.store'
  import { getCorrectedPrice } from '@/utils/formatter/price'
  import { useApplicationDetailsStore } from '@/stores/ApplicationDetailsView.store'
  import dayjs from 'dayjs'
  import { usePreferenceStore } from '@/stores/preferenceStore'
  import { Trash } from 'lucide-vue-next'

  const i18n = useI18n()
  const contractStore = useContractStore()
  const licenseStore = useLicenseStore()
  const preferenceStore = usePreferenceStore()
  const applicationDetailStore = useApplicationDetailsStore()

  const license = defineModel<SoftwareLicense>('license')
  const softwareLicenseCosts = ref<SoftwareLicenseCost>()

  const contractToEdit = ref<PriceInformation | null>()
  const contractToDelete = ref<PriceInformation | null>()

  const contractEditDialogVisible = ref(false)
  const contractDeleteDialogVisible = ref(false)
  const contractDeleteLoading = ref(false)
  const contractEditMethod = ref<'create' | 'edit'>('edit')
  const isFallback = ref<boolean>(false)

  const emptyForm = ref<PriceInformationPostIn>({
    _id: '',
    description: '',
    currency: preferenceStore.preferences?.currency_code ?? 'EUR',
  } as PriceInformationPostIn)

  const props = defineProps({
    drawerVisibility: {
      type: Boolean,
      required: true,
    },
    applicationId: {
      type: String,
      required: true,
    },
  })

  const emit = defineEmits(['update:drawerVisibility', 'delete', 'edit'])

  onMounted(() => {
    reloadCosts()
  })

  function handleContractSave() {
    contractEditDialogVisible.value = false
    reloadCosts()
  }

  function reloadCosts() {
    licenseStore
      .getSoftwareLicenseCosts({
        applicationId: props.applicationId,
        useCached: false,
      })
      .then((result) => {
        if (result && license.value)
          softwareLicenseCosts.value = result[license.value._id]
      })
  }

  const localVisibility = computed({
    get: () => props.drawerVisibility,
    set: (value) => emit('update:drawerVisibility', value),
  })

  watch(
    () => localVisibility.value,
    (newValue) => {
      if (newValue) reloadCosts()
    }
  )

  function handleLicenseDelete() {
    emit('delete', license)
  }

  const availableBillingPeriods = computed(() => {
    const periods: Record<string, string> = {}
    Object.values(BillingPeriod).forEach((enumValue) => {
      const label = i18n.te(`billingPeriodValues.${enumValue}`)
        ? i18n.t(`billingPeriodValues.${enumValue}`)
        : enumValue
      periods[enumValue] = label
    })
    return periods
  })

  /*
   * Case Management
   */

  const totalQuota = computed(() => {
    if (license.value && applicationDetailStore.quota[license.value?._id]) {
      const value = applicationDetailStore.quota[license.value?._id].quota
      return value ?? 0
    }

    return 0
  })

  const manualActiveQuota = computed(() => {
    return activeContracts.value.reduce((acc, item) => {
      return acc + (item.quota ?? 0)
    }, 0)
  })

  const externalQuota = computed(() => {
    return license.value?.quota_information?.quota ?? 0
  })

  const showAlertExternalExceeded = computed(() => {
    if (externalQuota.value && manualActiveQuota.value >= externalQuota.value)
      return true
    else return false
  })

  const showAlertExternalMissingContracts = computed(() => {
    if (!externalQuota.value) return false
    return !contracts.value || contracts.value.length === 0
  })

  /*
   * Contract Functions
   */

  const contracts = computed(() => {
    if (license.value) {
      return contractStore.getContract(license.value._id)
    }
    return undefined
  })

  const activeContracts = computed(() => {
    if (!contracts.value) return []
    return contracts.value.filter((item) => contractIsActive(item) === true)
  })

  function openCreateContractDialog() {
    // open dialog
    contractEditMethod.value = 'create'
    isFallback.value = false
    contractToEdit.value = emptyForm.value as PriceInformation
    contractEditDialogVisible.value = true
  }

  function openCreateFallbackContractDialog() {
    // open dialog
    contractEditMethod.value = 'create'
    isFallback.value = true
    contractToEdit.value = emptyForm.value as PriceInformation
    contractEditDialogVisible.value = true
    isFallback.value = true
  }

  function handleEditContract(contract: PriceInformation) {
    contractEditMethod.value = 'edit'
    isFallback.value = contract.quota === 0 ? true : false
    contractToEdit.value = contract
    contractEditDialogVisible.value = true
  }

  function handleDeleteContract(contract: PriceInformation) {
    contractToDelete.value = contract
    contractDeleteDialogVisible.value = true
  }

  async function removeContract(contract: PriceInformation) {
    const contractId = (contract as PriceInformation)._id

    if (license.value?._id) {
      await contractStore.deleteContract(license.value._id, contractId)
    }

    contractDeleteDialogVisible.value = false

    reloadCosts()
  }

  function contractDetailText(contract: PriceInformation) {
    const i18n = useI18n()

    // Handle Billing Unit
    let billingUnitText = ''
    if (
      contract.billing_unit &&
      contract.billing_unit !== BillingUnit.UNKNOWN
    ) {
      if (contract.billing_unit === BillingUnit.QUOTA && contract.quota === 0) {
        billingUnitText += `${i18n.t('externalQuota')}`
      } else {
        billingUnitText = i18n.t(`billingUnitValues.${contract.billing_unit}`)
        if (contract.quota) {
          billingUnitText += ` (${i18n.t('general.licenses', contract.quota)})`
        }
      }
    }

    // Handle Pricing
    let pricingText = ''
    if ((contract.price || contract.price === 0) && contract.currency) {
      pricingText = getCorrectedPrice(contract.price, contract.currency) ?? ''
      if (
        contract.billing_period &&
        contract.billing_period !== BillingPeriod.UNKNOWN
      ) {
        pricingText += ` / ${availableBillingPeriods.value[contract.billing_period]}`
      }
    }

    // Combine Texts
    return [billingUnitText, pricingText].filter(Boolean).join(' • ')
  }

  /*
   * Utils
   */

  function contractIsActive(
    item: PriceInformation
  ): 'deprecated' | 'upcoming' | boolean {
    const isBetween = dayjs().isBetween(item.from_date, item.end_date)
    const isAfter = dayjs().isAfter(item.end_date)
    const isBefore = dayjs().isBefore(item.from_date)

    if (isBetween || !item.from_date) {
      return true
    }

    if (isAfter) {
      return 'deprecated'
    }

    if (isBefore) {
      return 'upcoming'
    }

    return false
  }
</script>

<style scoped>
  .contract-item:last-of-type {
    border-bottom: unset;
  }
</style>
